import {ITeam, Team} from "./Team";
import {IUser, User} from "./User";
import {IContestant} from "../typings/IContestant";
import {IPager, Pager} from "./Pager";
import {Model} from "./Model";
import {IApiRanking, IApiRankingPoints, IApiRankingStats, IApiRankingEntry} from "../typings/apiResponse";
import * as angular from "angular";

export interface IRankingPoints {
    getTotal(): number
    getLastMatch(): number
    getTiebreakerValues(): number[]
}

export interface IRankingStats {
    getWins(): number
    getLosses(): number
    getDraws(): number
    getStreak(): number
}

export interface IRankingEntry {
    getPosition(): number
    getLabel(): string
    getStartSeeding(): number
    getSeed(): number
    isTeam(): boolean
    getCredibility(): number
    getNumOpenMatches(): number
    getActivity(): number
    getCredibility(): number
    getTeam(): ITeam
    getUser(): IUser
    getContestant(): IContestant
    getPoints(): IRankingPoints
    getStats(): IRankingStats
}

export interface IRanking {
    getType(): string
    getPager(): IPager
    getRanking(): Array<IRankingEntry>
    getTiebreakerTypes(): string[]
}

export class Ranking extends Model implements IRanking {
    constructor(private apiData:IApiRanking) {
        super(apiData);
    }

    getType():string {
        return this.apiData.type;
    }

    getPager():IPager {
        return new Pager(this.apiData.pager);
    }

    getRanking():Array<IRankingEntry> {
        var entries = [];

        angular.forEach(this.apiData.ranking, (item) => {
            entries.push(new RankingEntry(item));
        });

        return entries;
    }

    getTiebreakerTypes(): string[] {
        return this.apiData.tiebreakerTypes;
    }
}

export class RankingEntry extends Model implements IRankingEntry {
    constructor(private apiData:IApiRankingEntry) {
        super(apiData);
    }

    /**
     * @returns {number}
     */
    getPosition():number {
        return this.apiData.position;
    }

    /**
     * @returns {string}
     */
    getLabel():string {
        return this.apiData.label;
    }

    /**
     * Returns the team contestant if present
     * @returns {ITeam}
     */
    getTeam():ITeam {
        if (typeof this.apiData.team !== 'undefined') {
            return new Team(this.apiData.team);
        }

        return null;
    }

    /**
     * Returns the user contestant if present
     * @returns {IUser}
     */
    getUser():IUser {
        if (typeof this.apiData.user !== 'undefined') {
            return new User(this.apiData.user);
        }

        return null;
    }

    /**
     * Returns the contestant
     * @returns {IContestant}
     */
    getContestant():IContestant {
        if (this.isTeam()) {
            return this.getTeam();
        }

        return this.getUser();
    }

    /**
     * Returns the seeding of a contestant
     * @returns {number}
     */
    getSeed():number {
        return this.apiData.seed;
    }

    /**
     * @deprecated Use getSeed() instead
     * @returns {number}
     */
    getStartSeeding():number {
        return this.apiData.startSeeding;
    }

    /**
     * Returns if a contestant is a team
     * @returns {boolean}
     */
    isTeam():boolean {
        return typeof this.apiData.team !== 'undefined';
    }

    /**
     * Returns the reputation of a contestant
     * @returns {number}
     */
    getCredibility():number {
        return this.apiData.credibility;
    }

    /**
     * Returns the number of open matches of a contestant
     * @returns {number}
     */
    getNumOpenMatches():number {
        return this.apiData.numOpenMatches;
    }

    /**
     * Returns the activity of a contestant
     * @returns {number}
     */
    getActivity():number {
        return this.apiData.activity;
    }

    /**
     * Returns the ranking points of a contestant
     * @returns {IRankingPoints}
     */
    getPoints():IRankingPoints {
        if (typeof this.apiData.points !== 'undefined') {
            return new RankingPoints(this.apiData.points);
        }

        return null;
    }

    /**
     * Returns the win/draw/lose stats of a contestant
     * @returns {IRankingStats}
     */
    getStats():IRankingStats {
        if (typeof this.apiData.stats !== 'undefined') {
            return new RankingStats(this.apiData.stats);
        }

        return null;
    }
}

export class RankingPoints extends Model implements IRankingPoints {
    constructor(private apiData:IApiRankingPoints) {
        super(apiData);
    }

    getTotal():number {
        return this.apiData.total;
    }

    getLastMatch():number {
        return this.apiData.lastMatch;
    }

    getTiebreakerValues(): number[] {
        return this.apiData.tiebreakerValues;
    }
}

export class RankingStats extends Model implements IRankingStats {
    constructor(private apiData:IApiRankingStats) {
        super(apiData);
    }

    getWins():number {
        return this.apiData.wins;
    }

    getLosses():number {
        return this.apiData.losses;
    }

    getDraws():number {
        return this.apiData.draws;
    }

    getStreak():number {
        return this.apiData.streak;
    }
}
